<template lang="pug">
	section.section.homeContainer
		//- Take new measurements, use face recognition (add:measurements needed)
		.level(v-if="permissions.indexOf('add:measurements') > -1")
			.level-left
				.level-item: b-button(type="is-dark", @click="useFaceRecognition = false; takingMeasurement = true") New Measurement
				.level-item: b-button(type="is-primary", @click="useFaceRecognition = true; takingMeasurement = true") Face Recognition

		//- The table of data (read:measurements needed)
		b-table(:data="tableData", :paginated="true", :per-page="20", pagination-position="both", default-sort="timestamp", default-sort-direction="desc", :mobile-cards="false", bordered, striped, narrowed, hoverable, v-if="permissions.indexOf('read:measurements') > -1")
			template(slot-scope="props")
				b-table-column(field="timestamp", label="Timestamp", width="250", searchable, sortable)
					span {{ props.row.prettyTime }}
				b-table-column(field="org", label="Dept", width="100" searchable, sortable)
					span {{ props.row.org }}
				b-table-column(field="tag", label="ID", width="100" searchable, sortable)
					span {{ props.row.tag }}
				b-table-column(field="name", label="Name", searchable, sortable)
					span {{ props.row.name }}
				b-table-column(field="temp", label="Temperature")
					span {{ props.row.temp }}
				//- Column to delete measurements (delete:measurements needed)
				b-table-column(v-if="permissions.indexOf('delete:measurements') > -1")
					span
						a(@click="confirmDeleteRecord(props.row.id)")
							b-icon(type="is-danger", icon="trash", pack="fas")

			//- Buefy table slot "bottom-left" with download report functionality (download:measurements needed)
			template(slot="bottom-left", v-if="permissions.indexOf('download:measurements') > -1")
				.columns
					//- Filter by date and time
					.column
						//- Datetime module for start time filter
						b-field(label="Start")
							b-datetimepicker(v-model="startDatetime", placeholder="Click to select...")
								template(slot="left")
									button.button.is-primary(@click="startDatetime = new Date()")
										b-icon(icon="clock", pack="fas")
										span Now
								template(slot="right")
									button.button.is-danger(@click="startDatetime = null")
										b-icon(icon="times", pack="fas")
										span Clear
					.column
						//- Datetime module for end time filter
						b-field(label="End")
							b-datetimepicker(v-model="endDatetime", placeholder="Click to select...")
								template(slot="left")
									button.button.is-primary(@click="endDatetime = new Date()")
										b-icon(icon="clock", pack="fas")
										span Now
								template(slot="right")
									button.button.is-danger(@click="endDatetime = null")
										b-icon(icon="times", pack="fas")
										span Clear
					//- Download ADP report
					.column
						b-field(label="Generate Report")
							b-button(@click="downloadCSV") Download {{ startDatetime || endDatetime ? "Filtered" : "Full" }} Report

		//- Show Screen if no Permissions Set Yet
		template(v-if="permissions.length === 0")
			p Account not set up yet.

		//- Modal Container for NewMeasurement component
		b-modal(:active.sync="takingMeasurement", has-modal-card, trap-focus)
			NewMeasurement(:useFaceRecognition="useFaceRecognition")
</template>

<style lang="scss" scoped>
	//
</style>

<script>
	import { mapState, mapGetters } from "vuex";
	import moment from "moment";
	import "moment-timezone";

	export default {
		components: {
			NewMeasurement: () => import("@/components/NewMeasurement.vue")
		},
		data() {
			return {
				startDatetime: null, // filter table data by records that start by startDatetime
				endDatetime: null, // filter table data by records that end by endDatetime
				takingMeasurement: false, // show modal or not that contains NewMeasurement component
				useFaceRecognition: false // prop passed into NewMeasurement component if user presses "Use Face Recognition" instead of "New Measurement" since both open NewMeasurement component (in modal) but the NewMeasurement component will look differently based on if using face recognition or taking measurements manually
			};
		},
		computed: {
			...mapState(["records", "refreshedUser", "refreshedRecords", "permissions"]),
			...mapGetters(["employees"]),
			tableData() {
				// https://stackoverflow.com/questions/40348171/es6-map-an-array-of-objects-to-return-an-array-of-objects-with-new-keys
				// Map keys of records to a new set of keys with default values
				return (
					this.records
						.map((record) => ({
							id: record._id,
							org: record.org,
							timestamp: record.timestamp,
							prettyTime: moment(record.timestamp)
								.tz("Pacific/Honolulu")
								.format("MM/DD/YY, hh:mm A zz"),
							tag: record.employeeTag,
							temp: record.objectTemp,
							name: this.employees.find((employee) => employee._id === record.employeeID)
								? this.employees.find((employee) => employee._id === record.employeeID).name || ""
								: "EMPLOYEE NOT FOUND",
							notes: this.employees.find((employee) => employee._id === record.employeeID)
								? this.employees.find((employee) => employee._id === record.employeeID).notes || ""
								: ""
						}))
						// Filter by start & end date and time
						.filter((record) => {
							if (
								this.startDatetime &&
								moment(record.timestamp)
									.tz("Pacific/Honolulu")
									.isBefore(this.startDatetime)
							)
								return false; // this event's timestamp is BEFORE the filtered start datetime
							if (
								this.endDatetime &&
								moment(record.timestamp)
									.tz("Pacific/Honolulu")
									.isAfter(this.endDatetime)
							)
								return false; // this event's timestamp is AFTER the filtered end datetime
							return true; // this event's timestamp is within filtered start and end range
						})
				);
			}
		},
		methods: {
			// Ask to delete record before deleting record
			confirmDeleteRecord(recordID) {
				this.$buefy.dialog.confirm({
					message: "Are you sure you want to delete this measurement?",
					onConfirm: () => this.deleteRecord(recordID)
				});
			},
			// Delete a record
			deleteRecord(recordID) {
				this.$store
					.dispatch("deleteRecord", recordID)
					.then((res) => {
						// DEMO
						if (Object.keys(res).length === 0) {
							this.$buefy.snackbar.open({
								duration: 3000,
								message: "DEMO: Cannot delete this demo record.",
								type: "is-warning",
								position: "is-top-right",
								queue: false
							});
							return;
						}

						// param: res -- returns record that was recently deleted
						this.$buefy.snackbar.open({
							duration: 3000,
							message: "Successfully removed.",
							type: "is-success",
							position: "is-top-right",
							queue: false
						});
					})
					.catch((err) => {
						console.log(err);
						this.$buefy.snackbar.open({
							duration: 3000,
							message: "Unknown error occured.",
							type: "is-danger",
							position: "is-top-right"
						});
					});
			},
			// Download ADP Report
			downloadCSV() {
				// Map the row's headers to the ADP report's CSV headers
				const dataToDownload = this.tableData.map((row) => ({
					PERSONNUM: row.org,
					DATE: moment(row.timestamp)
						.tz("Pacific/Honolulu")
						.format("MM/DD/YYYY"),
					TIME: moment(row.timestamp)
						.tz("Pacific/Honolulu")
						.format("HH:mm"),
					LABORACCOUNT: row.tag
				}));
				// https://stackoverflow.com/questions/3749231/download-file-using-javascript-jquery
				function downloadFile(url, filename) {
					fetch(url)
						.then((resp) => resp.blob())
						.then((blob) => {
							const url = window.URL.createObjectURL(blob);
							const a = document.createElement("a");
							a.style.display = "none";
							a.href = url;
							// the filename you want
							a.download = filename || url.substring(url.lastIndexOf("/") + 1).split("?")[0];
							document.body.appendChild(a);
							a.click();
							window.URL.revokeObjectURL(url);
							document.body.removeChild(a);
						})
						.catch((err) => {
							console.log("Download failed!", err);
							this.$buefy.snackbar.open({
								duration: 3000,
								message: "Download failed.",
								type: "is-danger",
								position: "is-top-right"
							});
						});
				}
				// https://stackoverflow.com/questions/11257062/converting-json-object-to-csv-format-in-javascript
				// Also add double quotes to allow commas in the CSV file
				function convertToCSV(arr) {
					const array = [Object.keys(arr[0])].concat(arr);
					array.forEach((el) => {
						Object.keys(el).forEach((el2) => (el[el2] = `"${el[el2]}"`));
					});
					return array
						.map((it) => {
							return Object.values(it).toString();
						})
						.join("\n");
				}

				const now = new Date();
				downloadFile(
					"data:text/csv;charset=utf-8," + convertToCSV(dataToDownload), // download as CSV
					`log-${moment(now).format("MM-DD-HH-mm")}.csv` // name the file the current date & time
				);
			}
		},
		mounted() {
			if (!this.refreshedUser || !this.refreshedRecords) {
				this.$store.dispatch("refresh");
			}

			// Socket.io -- join a "chatroom" with a chatroom name of the user's MongoDB ID
			this.$socket.client.emit("joinRoomByID", this.$store.state.user._id);
		}
	};
</script>
